import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import sortingIcon from '@shared/assets/images/Sorting_icon.svg';

import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import SplitPageLayout from '@shared/components/organisms/SplitPageLayout/SplitPageLayout';
import Button from '@shared/components/atoms/Button/Button';
import Confirm from '@shared/components/atoms/Confirm/Confirm';

import useAuthStore from '@entities/auth/model/useAuthStore';
import useAlertStore from '@entities/ui/model/useAlertStore';
import useUnsavedLeaveStore, { UNSAVED_SCOPE } from '@shared/model/useUnsavedLeaveStore';
import {
  useGetAdminEmployeeListQuery,
  useCreateAdminEmployeeMutation,
  useUpdateAdminEmployeeMutation,
  useGetPositionListQuery,
  useGetRankListQuery,
} from '@features/employee/hooks';
import { useGetTeamByadminQuery } from '@features/team/hooks';
import { useConfirm } from '@shared/hooks';
import useSort from '@shared/hooks/useSort';
// 비밀번호 유효성 검사 사용 시 아래 주석 해제
// import validatePassword from '@features/user/utils/validatePassword';

import styles from './EmployeesManagementPage.module.scss';

/** 직원 저장 시 필수값 검사 알럿 메시지 (문구 변경·다국어 시 한 곳에서 관리) */
const VALIDATION_MESSAGES = {
  name: '이름을 입력해 주세요.',
  nameNoSpace: '이름에 공백을 포함할 수 없습니다.',
  employeeNumber: '사원번호는 8자리로 입력해야 합니다.',
  fimsEmpNo: 'FIMS 사원번호는 8자리로 입력해야 합니다.',
  employmentStatus: '재직상태를 선택해 주십시오.',
  position: '직급을 선택해 주세요.',
  jobTitle: '직책을 선택해 주세요.',
  userId: '아이디를 입력해 주세요.',
  password: '비밀번호를 입력해 주세요.',
  email: '사내 이메일을 입력해 주세요.',
  emailFormat: '사내 이메일 형식이 올바르지 않습니다.',
  departmentNoManager: '관리자가 지정되지 않는 부서입니다. 관리자를 우선 지정해주세요.',
};

/**
 * EmployeesManagementPage
 * - 관리자 직원관리 페이지
 */
export default function EmployeesManagementPage() {
  // 사용자 회사코드 가져오기
  const companyCode = useAuthStore((state) => state.companyCode);

  const [filterValues, setFilterValues] = useState({
    parentDepartment: '',
    department: '',
    department2: '',
    department3: '',
    employeeName: '',
    employmentStatus: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    parentDepartment: '',
    department: '',
    department2: '',
    department3: '',
    employeeName: '',
    employmentStatus: '',
  });

  // 선택된 직원 정보
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  // 저장 없이 이동 시 확인용: 선택/추가 시점 스냅샷, 폼 변경 시 dirty
  const [isDirty, setIsDirty] = useState(false);
  const initialSelectedRef = useRef(null);

  const { showAlert } = useAlertStore();
  const navigate = useNavigate();
  const { confirmState, showConfirm, closeConfirm } = useConfirm();
  const setUnsaved = useUnsavedLeaveStore((state) => state.setUnsaved);
  const clearUnsaved = useUnsavedLeaveStore((state) => state.clearUnsaved);
  const pendingLeave = useUnsavedLeaveStore((state) => state.pendingLeave);
  const confirmLeave = useUnsavedLeaveStore((state) => state.confirmLeave);
  const cancelLeave = useUnsavedLeaveStore((state) => state.cancelLeave);

  // 부서 목록 조회 (조직관리 /admin/organization 과 동일: 관리자 API + 운영 중인 부서만)
  const adminTeamQuery = useGetTeamByadminQuery(companyCode, {
    enabled: !!companyCode,
  });
  const rawTeamList = adminTeamQuery.data?.resultData?.teamList ?? [];
  const teamList = useMemo(() => rawTeamList.filter((team) => team.enabled === 0), [rawTeamList]);

  // 조직 관리자 사원번호 수집용 (동일 소스, 직원 저장 시 휴직/퇴직 검사)
  const adminTeamList = teamList;

  // 조직 관리자 사원번호 집합 (휴직/퇴직 저장 시 검사용)
  const managerEnumberSet = useMemo(
    () => new Set(adminTeamList.map((t) => Number(t.enumber)).filter(Boolean)),
    [adminTeamList],
  );

  // 부서 코드로 부서명 매핑
  const teamMap = useMemo(() => {
    const map = new Map();
    teamList.forEach((team) => {
      map.set(team.teamCode, team);
    });
    return map;
  }, [teamList]);

  // 상위부서 코드로 부서명 매핑 (부서코드 -> 상위부서명)
  const parentDepartmentMap = useMemo(() => {
    const map = new Map();
    teamList.forEach((team) => {
      if (team.departmentCode && team.departmentCode !== 0) {
        const parentTeam = teamList.find((t) => t.teamCode === team.departmentCode);
        if (parentTeam) {
          map.set(team.teamCode, parentTeam.team);
        }
      }
    });
    return map;
  }, [teamList]);

  // 1뎁스(루트) 부서 목록 - departmentCode가 0이거나 없는 팀만
  const parentDepartmentList = useMemo(() => {
    const roots = teamList.filter((team) => !team.departmentCode || team.departmentCode === 0);
    return roots.map((t) => t.team).sort();
  }, [teamList]);

  /**
   * teamCode로부터 1~4뎁스 부서 경로 반환 (루트 → 리프 순)
   * @param {number} teamCode - 팀(부서) 코드
   * @param {Array} list - teamList
   * @returns {{ parentDepartment: string, department: string, department2: string, department3: string }}
   */
  function getDepartmentPath(teamCode, list) {
    const path = [];
    let current = list.find((t) => t.teamCode === teamCode);
    while (current) {
      path.unshift(current.team);
      if (!current.departmentCode || current.departmentCode === 0) break;
      current = list.find((t) => t.teamCode === current.departmentCode);
    }
    return {
      parentDepartment: path[0] || '',
      department: path[1] || '',
      department2: path[2] || '',
      department3: path[3] || '',
    };
  }

  /**
   * teamCode(리프)로부터 루트~리프까지의 teamCode 배열 반환 (부서명 중복 시 올바른 경로 식별용)
   * @param {number} teamCode - 팀(부서) 코드 (리프)
   * @param {Array} list - teamList
   * @returns {number[]} [1뎁스 teamCode, 2뎁스 teamCode, 3뎁스 teamCode?, 4뎁스 teamCode?]
   */
  function getDepartmentPathCodes(teamCode, list) {
    const pathCodes = [];
    let current = list.find((t) => t.teamCode === teamCode);
    while (current) {
      pathCodes.unshift(current.teamCode);
      if (!current.departmentCode || current.departmentCode === 0) break;
      current = list.find((t) => t.teamCode === current.departmentCode);
    }
    return pathCodes;
  }

  // 선택된 직원의 부서 경로(teamCode 배열) - 부서명 중복 시 올바른 하위만 표시하기 위함
  const selectedPathCodes = useMemo(() => {
    const code = selectedEmployee?.teamCode;
    if (!code || !teamList.length) return [];
    return getDepartmentPathCodes(code, teamList);
  }, [selectedEmployee?.teamCode, teamList]);

  // 검색필터: 선택된 상위부서에 해당하는 부서 목록 (상위부서 선택 시 해당 하위 부서만 노출)
  const filterDepartmentOptions = useMemo(() => {
    if (!filterValues.parentDepartment || filterValues.parentDepartment === '전체') {
      return ['전체', ...new Set(teamList.map((team) => team.team))];
    }
    const parentTeam = teamList.find((t) => t.team === filterValues.parentDepartment);
    if (!parentTeam) {
      return ['전체'];
    }
    const subTeams = teamList.filter((team) => team.departmentCode === parentTeam.teamCode);
    return ['전체', ...subTeams.map((team) => team.team)];
  }, [teamList, filterValues.parentDepartment]);

  // 선택된 상위부서(1뎁스)에 해당하는 부서 목록 (우측 패널용, 2뎁스) — teamCode 기준으로만 계산 (부서명 중복 대응)
  const filteredTeamListByParent = useMemo(() => {
    const rootCode = selectedPathCodes[0];
    if (rootCode != null) {
      return teamList.filter((team) => team.departmentCode === rootCode);
    }
    // 신규 직원 등 teamCode 없이 상위부서만 선택한 경우: 1뎁스는 이름이 보통 유일하므로 이름으로 1뎁스 찾기
    if (!selectedEmployee?.parentDepartment) return [];
    const rootTeam = teamList.find(
      (t) =>
        t.team === selectedEmployee.parentDepartment &&
        (!t.departmentCode || t.departmentCode === 0),
    );
    if (!rootTeam) return [];
    return teamList.filter((team) => team.departmentCode === rootTeam.teamCode);
  }, [teamList, selectedEmployee?.parentDepartment, selectedPathCodes]);

  // 선택된 부서(2뎁스)의 하위 목록 (우측 패널용, 3뎁스) — teamCode 경로 기준
  const level3Options = useMemo(() => {
    const parentCode = selectedPathCodes[1];
    if (parentCode == null) return [];
    return teamList.filter((t) => t.departmentCode === parentCode);
  }, [teamList, selectedPathCodes]);

  // 선택된 부서2(3뎁스)의 하위 목록 (우측 패널용, 4뎁스) — teamCode 경로 기준
  const level4Options = useMemo(() => {
    const parentCode = selectedPathCodes[2];
    if (parentCode == null) return [];
    return teamList.filter((t) => t.departmentCode === parentCode);
  }, [teamList, selectedPathCodes]);

  // 부서2 드롭다운 노출: 선택된 경로의 2뎁스가 있고, 그 부서에 실제 하위부서가 있을 때만
  const showDepartment2 = useMemo(() => {
    const parentCode = selectedPathCodes[1];
    if (parentCode == null) return false;
    return teamList.some((t) => t.departmentCode === parentCode);
  }, [teamList, selectedPathCodes]);

  // 부서3 드롭다운 노출: 선택된 경로의 3뎁스가 있고, 그 부서에 실제 하위부서가 있을 때만
  const showDepartment3 = useMemo(() => {
    const parentCode = selectedPathCodes[2];
    if (parentCode == null) return false;
    return teamList.some((t) => t.departmentCode === parentCode);
  }, [teamList, selectedPathCodes]);

  // 필터용 3뎁스 옵션 (선택된 부서 하위)
  const filterDepartment2Options = useMemo(() => {
    if (!filterValues.department || filterValues.department === '전체') return ['전체'];
    const parentTeam = teamList.find((t) => t.team === filterValues.department);
    if (!parentTeam) return ['전체'];
    const sub = teamList.filter((t) => t.departmentCode === parentTeam.teamCode);
    return ['전체', ...sub.map((t) => t.team)];
  }, [teamList, filterValues.department]);

  // 필터용 4뎁스 옵션 (선택된 부서2 하위)
  const filterDepartment3Options = useMemo(() => {
    if (!filterValues.department2 || filterValues.department2 === '전체') return ['전체'];
    const parentTeam = teamList.find((t) => t.team === filterValues.department2);
    if (!parentTeam) return ['전체'];
    const sub = teamList.filter((t) => t.departmentCode === parentTeam.teamCode);
    return ['전체', ...sub.map((t) => t.team)];
  }, [teamList, filterValues.department2]);

  const searchPayload = useMemo(() => {
    // 최종 선택된 부서(리프)의 teamCode 사용: department3 > department2 > department
    let departmentTeamCode = 0;
    if (appliedFilters.department3 && appliedFilters.department3 !== '전체') {
      departmentTeamCode =
        teamList.find((team) => team.team === appliedFilters.department3)?.teamCode || 0;
    } else if (appliedFilters.department2 && appliedFilters.department2 !== '전체') {
      departmentTeamCode =
        teamList.find((team) => team.team === appliedFilters.department2)?.teamCode || 0;
    } else if (appliedFilters.department && appliedFilters.department !== '전체') {
      departmentTeamCode =
        teamList.find((team) => team.team === appliedFilters.department)?.teamCode || 0;
    }

    return {
      company: Number(companyCode) || 0,
      enumber: 0,
      team: Number(departmentTeamCode) || 0,
      name: appliedFilters.employeeName || '',
    };
  }, [
    appliedFilters.department,
    appliedFilters.department2,
    appliedFilters.department3,
    appliedFilters.employeeName,
    companyCode,
    teamList,
  ]);

  // 관리자 직원 목록 조회 (사용자 회사코드 기반)
  const employeeListQuery = useGetAdminEmployeeListQuery(searchPayload, {
    enabled: !!companyCode,
  });
  const employeeList = employeeListQuery.data?.resultData?.empList || [];

  // 직원 저장 Mutation
  const createEmployeeMutation = useCreateAdminEmployeeMutation({
    onSuccess: (response) => {
      // 서버가 200으로 응답했지만 error_msg가 있으면 유효성 검사 실패 → 확인 알럿으로 메시지 표시
      const errorMessage = response?.error_msg || response?.message;
      if (errorMessage) {
        showAlert(errorMessage);
        return;
      }
      showAlert('저장되었습니다.', () => {
        employeeListQuery.refetch();
      });
      setIsDirty(false);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error_msg ||
        error?.response?.data?.message ||
        error?.message ||
        '저장 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    },
  });

  const updateEmployeeMutation = useUpdateAdminEmployeeMutation({
    onSuccess: (response) => {
      // 성공: result_code === 0 이고 error_msg가 없을 때만 (백엔드가 예외 시 result_code 0 + error_msg 로 보낼 수 있음)
      const hasValidationError = response?.error_msg || response?.message;
      if (response && response.result_code === 0 && !hasValidationError) {
        showAlert('저장되었습니다.', () => {
          employeeListQuery.refetch();
        });
        setIsDirty(false);
        return;
      }

      // 유효성 검사 실패(아이디 중복 등): 백엔드 메시지를 확인 알럿으로 표시
      const errorMessage =
        response?.error_msg || response?.message || '저장 중 오류가 발생했습니다.';

      if (errorMessage.includes('기존 비밀번호와 일치합니다')) {
        showAlert('새 비밀번호는 기존 비밀번호와 달라야 합니다.', () => {
          setSelectedEmployee((prev) => {
            if (!prev) return null;
            return { ...prev, password: '' };
          });
        });
      } else {
        showAlert(errorMessage);
      }
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error_msg ||
        error?.response?.data?.message ||
        error?.message ||
        '저장 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    },
  });

  // 저장 없이 이동 시 확인: LNB/로그아웃 시 requestLeave → pendingLeave → Confirm 표시
  useEffect(
    function () {
      if (isDirty) {
        setUnsaved(UNSAVED_SCOPE.EMPLOYEE_MANAGEMENT, function getResetAndProceed() {
          return function runThenNext(next) {
            clearUnsaved();
            if (typeof next === 'function') next();
          };
        });
      } else {
        clearUnsaved();
      }
      return function cleanup() {
        clearUnsaved();
      };
    },
    [isDirty, setUnsaved, clearUnsaved],
  );

  // 직급/직책 목록 조회
  const rankListQuery = useGetRankListQuery({
    enabled: !!companyCode,
  });
  const positionListQuery = useGetPositionListQuery({
    enabled: !!companyCode,
  });
  const rankList = rankListQuery.data?.resultData?.rankList || [];
  const positionList = positionListQuery.data?.resultData?.positionList || [];

  // 필터 필드 정의
  const filterFields = useMemo(
    () => [
      // 1열: 상위부서 | 부서
      {
        key: 'parentDepartment',
        type: 'select',
        label: '상위부서',
        value: filterValues.parentDepartment || '',
        options: ['전체', ...parentDepartmentList],
        fill: true,
      },
      {
        key: 'department',
        type: 'select',
        label: '부서',
        value: filterValues.department || '',
        options: filterDepartmentOptions,
        fill: true,
      },
      { newRow: true },
      // 2열: 부서2 | 부서3
      {
        key: 'department2',
        type: 'select',
        label: '부서2',
        value: filterValues.department2 || '',
        options: filterDepartment2Options,
        fill: true,
      },
      {
        key: 'department3',
        type: 'select',
        label: '부서3',
        value: filterValues.department3 || '',
        options: filterDepartment3Options,
        fill: true,
      },
      { newRow: true },
      // 3열: 재직상태 | 임직원명
      {
        key: 'employmentStatus',
        type: 'select',
        label: '재직상태',
        value: filterValues.employmentStatus || '',
        options: ['전체', '재직', '휴직', '퇴직'],
        fill: true,
      },
      {
        key: 'employeeName',
        type: 'input',
        label: '임직원명',
        placeholder: '',
        value: filterValues.employeeName || '',
        wide: true,
      },
    ],
    [
      filterValues,
      parentDepartmentList,
      filterDepartmentOptions,
      filterDepartment2Options,
      filterDepartment3Options,
    ],
  );

  // 테이블 컬럼 정의 (부서 = 항상 최하위 부서명만 표시)
  const tableColumns = useMemo(
    () => [
      {
        key: 'employeeNumber',
        label: '사원번호',
        width: '18%',
        sortable: false,
      },
      {
        key: 'name',
        label: '임직원명',
        width: '14%',
        sortable: false,
      },
      {
        key: 'employmentStatus',
        label: '재직상태',
        width: '12%',
        sortable: false,
      },
      {
        key: 'parentDepartment',
        label: '상위부서',
        width: '19%',
        sortable: false,
      },
      {
        key: 'departmentDisplay',
        label: '부서',
        width: '19%',
        sortable: false,
      },
      {
        key: 'isAdmin',
        label: '관리자',
        width: '18%',
        sortable: true,
        render(value) {
          return value ? 'O' : '-';
        },
      },
    ],
    [],
  );

  // 테이블 데이터 변환 및 필터링 (부서 컬럼 = 최하위 부서명, 필터는 경로 유지)
  const tableData = useMemo(() => {
    let filtered = employeeList.map((emp) => {
      const path = getDepartmentPath(emp.team, teamList);
      // 표시용: 항상 제일 하위 부서명 (리프)
      const departmentDisplay =
        path.department3 || path.department2 || path.department || path.parentDepartment || '';

      let status = '재직';
      if (emp.retired === 1) {
        status = '퇴직';
      } else if (emp.retired === 2) {
        status = '휴직';
      }

      return {
        ...emp,
        employeeNumber: emp.enumber,
        name: emp.name,
        parentDepartment: path.parentDepartment,
        department: path.department,
        department2: path.department2,
        department3: path.department3,
        departmentDisplay,
        employmentStatus: status,
        isAdmin: emp.admin === 1,
        teamCode: emp.team,
      };
    });

    if (appliedFilters.parentDepartment && appliedFilters.parentDepartment !== '전체') {
      filtered = filtered.filter((emp) => emp.parentDepartment === appliedFilters.parentDepartment);
    }

    if (appliedFilters.department && appliedFilters.department !== '전체') {
      filtered = filtered.filter((emp) => emp.department === appliedFilters.department);
    }

    if (appliedFilters.department2 && appliedFilters.department2 !== '전체') {
      filtered = filtered.filter((emp) => emp.department2 === appliedFilters.department2);
    }

    if (appliedFilters.department3 && appliedFilters.department3 !== '전체') {
      filtered = filtered.filter((emp) => emp.department3 === appliedFilters.department3);
    }

    if (appliedFilters.employeeName) {
      filtered = filtered.filter((emp) => emp.name.includes(appliedFilters.employeeName));
    }

    if (appliedFilters.employmentStatus && appliedFilters.employmentStatus !== '전체') {
      filtered = filtered.filter((emp) => emp.employmentStatus === appliedFilters.employmentStatus);
    }

    // 1순위: 상위부서(값 있음 위, 빈값 아래) 가나다순, 2순위: 하위부서 동일, 3순위: 임직원명 가나다순
    filtered = [...filtered].sort(function (a, b) {
      const hasVal = (v) => Boolean(v && String(v).trim());
      const cmpWithEmptyLast = (va, vb) => {
        const aEmpty = !hasVal(va);
        const bEmpty = !hasVal(vb);
        if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
        return (va || '').localeCompare(vb || '', 'ko');
      };
      const pa = cmpWithEmptyLast(a.parentDepartment, b.parentDepartment);
      if (pa !== 0) return pa;
      const da = cmpWithEmptyLast(a.department, b.department);
      if (da !== 0) return da;
      const d2 = cmpWithEmptyLast(a.department2, b.department2);
      if (d2 !== 0) return d2;
      const d3 = cmpWithEmptyLast(a.department3, b.department3);
      if (d3 !== 0) return d3;
      return (a.name || '').localeCompare(b.name || '', 'ko');
    });

    return filtered;
  }, [employeeList, teamList, appliedFilters]);

  function handleFilterChange(key, value) {
    setFilterValues((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'parentDepartment') {
        next.department = '';
        next.department2 = '';
        next.department3 = '';
        if (value && value !== '전체') {
          const parentTeam = teamList.find((t) => t.team === value);
          if (parentTeam) {
            const subTeams = teamList.filter((team) => team.departmentCode === parentTeam.teamCode);
            const subTeamNames = new Set(subTeams.map((t) => t.team));
            if (
              prev.department &&
              prev.department !== '전체' &&
              !subTeamNames.has(prev.department)
            ) {
              next.department = '';
            }
          }
        }
      } else if (key === 'department') {
        next.department2 = '';
        next.department3 = '';
      } else if (key === 'department2') {
        next.department3 = '';
      }
      return next;
    });
  }

  function handleSearch() {
    setAppliedFilters(filterValues);
    employeeListQuery.refetch();
  }

  // 필터 초기화 핸들러
  function handleReset() {
    const initialFilters = {
      parentDepartment: '',
      department: '',
      department2: '',
      department3: '',
      employeeName: '',
      employmentStatus: '',
    };
    setFilterValues(initialFilters);
    setAppliedFilters(initialFilters);
    setSelectedEmployee(null);
    initialSelectedRef.current = null;
    setIsDirty(false);
  }

  /** 리스트 행 데이터를 우측 패널 폼 객체로 변환 */
  function itemToFormEmployee(item) {
    return {
      company: companyCode || '',
      employeeNumber: item.employeeNumber || '',
      name: item.name || '',
      parentDepartment: item.parentDepartment || '',
      department: item.department || '',
      department2: item.department2 || '',
      department3: item.department3 || '',
      teamCode: item.teamCode || '',
      position: item.rank ? String(item.rank) : '',
      jobTitle: item.position ? String(item.position) : '',
      employmentStatus: item.employmentStatus || '재직',
      fimsEmpNo: item.fimsEmpNo || '',
      userId: item.id || '',
      isAdmin: item.isAdmin || false,
      password: '',
      email: item.email || '',
      empIdx: item.empIdx || '',
      notEnforcer: item.notEnforcer === 1, // 참조자 제외 여부 1일때 제외
      allowConcurrentLogin: item.allowConcurrentLogin === 1,
    };
  }

  /** 빈 직원 추가 폼 객체 */
  function getEmptyFormEmployee() {
    return {
      company: companyCode || '',
      employeeNumber: '',
      name: '',
      parentDepartment: '',
      department: '',
      department2: '',
      department3: '',
      teamCode: '',
      position: '',
      jobTitle: '',
      employmentStatus: '',
      fimsEmpNo: '',
      userId: '',
      isAdmin: false,
      password: '',
      email: '',
      empIdx: '',
      notEnforcer: false,
      allowConcurrentLogin: false,
    };
  }

  function handleRowClick(item) {
    if (isDirty) {
      showConfirm('작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?', function () {
        clearUnsaved();
        closeConfirm();
        const next = itemToFormEmployee(item);
        initialSelectedRef.current = { ...next };
        setSelectedEmployee(next);
        setIsDirty(false);
      });
      return;
    }
    const next = itemToFormEmployee(item);
    initialSelectedRef.current = { ...next };
    setSelectedEmployee(next);
    setIsDirty(false);
  }

  /**
   * 선택된 부서 경로에서 리프(최하위) 부서의 teamCode 반환
   */
  function getLeafTeamCode(sel, list) {
    if (!sel || !list) return 0;
    if (sel.department3) {
      const t = list.find((x) => x.team === sel.department3);
      return t ? t.teamCode : 0;
    }
    if (sel.department2) {
      const t = list.find((x) => x.team === sel.department2);
      return t ? t.teamCode : 0;
    }
    if (sel.department) {
      const t = list.find((x) => x.team === sel.department);
      return t ? t.teamCode : 0;
    }
    if (sel.parentDepartment) {
      const t = list.find((x) => x.team === sel.parentDepartment);
      return t ? t.teamCode : 0;
    }
    return 0;
  }

  function handleEmployeeInfoChange(key, value) {
    setIsDirty(true);
    setSelectedEmployee((prev) => {
      if (!prev) {
        return null;
      }

      if (key === 'parentDepartment') {
        const rootTeam =
          value &&
          teamList.find((t) => t.team === value && (!t.departmentCode || t.departmentCode === 0));
        return {
          ...prev,
          [key]: value,
          department: '',
          department2: '',
          department3: '',
          teamCode: rootTeam ? rootTeam.teamCode : '',
        };
      }

      if (key === 'department') {
        const pathCodes = getDepartmentPathCodes(prev.teamCode, teamList);
        const rootCode =
          pathCodes[0] ??
          teamList.find(
            (t) =>
              t.team === prev.parentDepartment && (!t.departmentCode || t.departmentCode === 0),
          )?.teamCode;
        const team =
          rootCode != null
            ? teamList.find((t) => t.team === value && t.departmentCode === rootCode)
            : teamList.find((t) => t.team === value);
        return {
          ...prev,
          [key]: value,
          department2: '',
          department3: '',
          teamCode: team ? team.teamCode : '',
        };
      }

      if (key === 'department2') {
        const pathCodes = getDepartmentPathCodes(prev.teamCode, teamList);
        const parentCode = pathCodes[1];
        const team =
          parentCode != null
            ? teamList.find((t) => t.team === value && t.departmentCode === parentCode)
            : teamList.find((t) => t.team === value);
        return {
          ...prev,
          [key]: value,
          department3: '',
          teamCode: team ? team.teamCode : '',
        };
      }

      if (key === 'department3') {
        const pathCodes = getDepartmentPathCodes(prev.teamCode, teamList);
        const parentCode = pathCodes[2];
        const team =
          parentCode != null
            ? teamList.find((t) => t.team === value && t.departmentCode === parentCode)
            : teamList.find((t) => t.team === value);
        return {
          ...prev,
          [key]: value,
          teamCode: team ? team.teamCode : prev.teamCode,
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  }

  function handleSave() {
    if (!selectedEmployee) {
      return;
    }

    // 이름 필수 입력 및 공백 검사
    const nameValue = selectedEmployee.name || '';
    if (nameValue.length === 0) {
      showAlert(VALIDATION_MESSAGES.name);
      return;
    }
    if (/\s/.test(nameValue)) {
      showAlert(VALIDATION_MESSAGES.nameNoSpace);
      return;
    }

    // 사원번호 8자리 검사
    const employeeNumberStr = String(selectedEmployee.employeeNumber ?? '').trim();
    if (employeeNumberStr.length !== 8 || !/^\d{8}$/.test(employeeNumberStr)) {
      showAlert(VALIDATION_MESSAGES.employeeNumber);
      return;
    }

    // FIMS 사원번호 8자리 검사
    const fimsEmpNoStr = String(selectedEmployee.fimsEmpNo ?? '').trim();
    if (fimsEmpNoStr.length !== 8 || !/^\d{8}$/.test(fimsEmpNoStr)) {
      showAlert(VALIDATION_MESSAGES.fimsEmpNo);
      return;
    }

    // 재직상태 필수 선택 검사
    const validStatuses = ['재직', '휴직', '퇴직'];
    const status = (selectedEmployee.employmentStatus || '').trim();
    if (!validStatuses.includes(status)) {
      showAlert(VALIDATION_MESSAGES.employmentStatus);
      return;
    }

    // 직급 필수 선택 검사
    const positionStr = String(selectedEmployee.position ?? '').trim();
    if (!positionStr) {
      showAlert(VALIDATION_MESSAGES.position);
      return;
    }

    // 직책 필수 선택 검사
    const jobTitleStr = String(selectedEmployee.jobTitle ?? '').trim();
    if (!jobTitleStr) {
      showAlert(VALIDATION_MESSAGES.jobTitle);
      return;
    }

    // 아이디 필수 입력 검사
    const userIdStr = (selectedEmployee.userId || '').trim();
    if (!userIdStr) {
      showAlert(VALIDATION_MESSAGES.userId);
      return;
    }

    // 직원 추가 시 비밀번호 필수
    const isNewEmployee = !selectedEmployee.empIdx;
    const passwordStr = (selectedEmployee.password || '').trim();
    if (isNewEmployee && !passwordStr) {
      showAlert(VALIDATION_MESSAGES.password);
      return;
    }

    // 사내 이메일 필수 입력 및 형식 검사 (@ 포함, 도메인 일부)
    const emailStr = (selectedEmployee.email || '').trim();
    if (!emailStr) {
      showAlert(VALIDATION_MESSAGES.email);
      return;
    }
    const emailFormatRegex = /^[^@]+@[^@]+\.[^@]+$/;
    if (!emailFormatRegex.test(emailStr)) {
      showAlert(VALIDATION_MESSAGES.emailFormat);
      return;
    }

    // 비밀번호 유효성 검사 (필요 시 아래 주석 해제)
    // if (selectedEmployee.password && !validatePassword(selectedEmployee.password)) {
    //   showAlert('비밀번호 생성 규칙을 확인해 주세요.');
    //   return;
    // }

    // 부서명 중복 시 올바른 부서 반영: 폼에 저장된 teamCode 우선 사용
    const teamCode = selectedEmployee.teamCode || getLeafTeamCode(selectedEmployee, teamList) || 0;

    // 선택한 부서에 관리자가 있는지 검사 (없으면 저장 차단)
    if (Number(teamCode) > 0) {
      const selectedTeam = teamList.find((t) => t.teamCode === Number(teamCode));
      if (selectedTeam && !Number(selectedTeam.enumber)) {
        showAlert(VALIDATION_MESSAGES.departmentNoManager);
        return;
      }
    }

    // 재직상태 매핑 (0: 재직, 1: 퇴직, 2: 휴직)
    let retired = 0;
    if (selectedEmployee.employmentStatus === '퇴직') {
      retired = 1;
    } else if (selectedEmployee.employmentStatus === '휴직') {
      retired = 2;
    }

    // 휴직/퇴직으로 변경 시 조직 관리자 여부 검사
    if (
      (retired === 1 || retired === 2) &&
      managerEnumberSet.has(Number(selectedEmployee.employeeNumber))
    ) {
      showConfirm(
        '해당 임직원은 현재 조직의 관리자로 지정되어있습니다.\n관리자를 먼저 변경해주세요.',
        function onConfirm() {
          closeConfirm();
          navigate('/admin/organization');
        },
      );
      return;
    }

    const payload = {
      company: Number(companyCode) || 0,
      team: Number(teamCode) || 0,
      enumber: Number(selectedEmployee.employeeNumber) || 0,
      name: selectedEmployee.name || '',
      rank: Number(selectedEmployee.position) || 0,
      position: Number(selectedEmployee.jobTitle) || 0,
      email: selectedEmployee.email || '',
      id: selectedEmployee.userId || '',
      pw: selectedEmployee.password || '',
      empIdx: Number(selectedEmployee.empIdx) || 0,
      admin: selectedEmployee.isAdmin ? 1 : 0,
      retired: retired,
      notEnforcer: selectedEmployee.notEnforcer ? 1 : 0,
      allowConcurrentLogin: selectedEmployee.allowConcurrentLogin ? 1 : 0,
      fimsEmpNo: fimsEmpNoStr,
    };

    if (selectedEmployee.empIdx) {
      updateEmployeeMutation.mutate(payload);
      return;
    }

    createEmployeeMutation.mutate(payload);
  }

  // 사원번호 표시/검증용 (숫자 타입도 문자열로 통일)
  const employeeNumberDisplay = useMemo(
    () => String(selectedEmployee?.employeeNumber ?? ''),
    [selectedEmployee?.employeeNumber],
  );
  const showEmployeeNumberError =
    employeeNumberDisplay.length > 0 && employeeNumberDisplay.length !== 8;
  const fimsEmpNoDisplay = useMemo(
    () => String(selectedEmployee?.fimsEmpNo ?? ''),
    [selectedEmployee?.fimsEmpNo],
  );
  const showFimsEmpNoError = fimsEmpNoDisplay.length > 0 && fimsEmpNoDisplay.length !== 8;
  const { sortedData, sortField, sortDirection, handleSort } = useSort(tableData);

  return (
    <AdminLayout>
      <TabContentLayout
        tabs={{
          label: '직원관리',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              {/* 검색/필터 섹션 */}
              <div className={styles.filterWrapper}>
                <SearchFilter
                  fields={filterFields}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  showExcelButton={false}
                  empCount={tableData.length}
                />
              </div>

              {/* 좌우 분할 레이아웃 */}
              <SplitPageLayout
                leftWidth="70%"
                rightWidth="30%"
                height="calc(100% - 0px)"
                leftChildren={
                  <>
                    <div className={styles.tableHeader}>
                      <Button
                        type="button"
                        className={styles.addButton}
                        onClick={function () {
                          const empty = getEmptyFormEmployee();
                          if (isDirty) {
                            showConfirm(
                              '작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?',
                              function () {
                                clearUnsaved();
                                closeConfirm();
                                initialSelectedRef.current = { ...empty };
                                setSelectedEmployee(empty);
                                setIsDirty(false);
                              },
                            );
                            return;
                          }
                          initialSelectedRef.current = { ...empty };
                          setSelectedEmployee(empty);
                          setIsDirty(false);
                        }}
                      >
                        + 직원 추가
                      </Button>
                    </div>
                    <div className={styles.tableWrapper}>
                      <div className={styles.tableContainer}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              {tableColumns.map(function (column) {
                                return (
                                  <th
                                    key={column.key}
                                    className={`${styles.tableHeaderCell} ${
                                      column.sortable ? styles.sortable : ''
                                    }`}
                                    style={column.width ? { width: column.width } : undefined}
                                    onClick={
                                      column.sortable ? () => handleSort(column.key) : undefined
                                    }
                                  >
                                    <div className={styles.headerInner}>
                                      <span>{column.label}</span>

                                      {column.sortable && (
                                        <img
                                          src={sortingIcon}
                                          alt=""
                                          className={styles.sortingIcon}
                                        />
                                      )}
                                    </div>
                                  </th>
                                  // <th
                                  //   key={column.key}
                                  //   className={styles.tableHeaderCell}
                                  //   style={column.width ? { width: column.width } : undefined}
                                  //   onClick={() => {
                                  //     if (column.sortable) {
                                  //       handleSort(column.key);
                                  //     }
                                  //   }}
                                  // >
                                  //   {column.label}
                                  // </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {tableData.length === 0 ? (
                              <tr>
                                <td colSpan={tableColumns.length} className={styles.emptyCell}>
                                  직원 정보가 없습니다.
                                </td>
                              </tr>
                            ) : (
                              sortedData.map(function (item, index) {
                                const isSelected =
                                  selectedEmployee &&
                                  Number(item.empIdx) !== 0 &&
                                  Number(item.empIdx) === Number(selectedEmployee.empIdx);
                                return (
                                  <tr
                                    key={`${item.employeeNumber}_${index}`}
                                    className={`${styles.tableRow} ${isSelected ? styles.tableRowSelected : ''}`}
                                    onClick={function () {
                                      handleRowClick(item);
                                    }}
                                  >
                                    {tableColumns.map(function (column) {
                                      const cellValue = column.render
                                        ? column.render(item[column.key], item, index)
                                        : item[column.key];

                                      return (
                                        <td
                                          key={column.key}
                                          className={styles.tableCell}
                                          style={column.width ? { width: column.width } : undefined}
                                        >
                                          {cellValue}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                }
                rightChildren={
                  <>
                    {/* 우측 패널 헤더 */}
                    <div className={styles.rightPanelHeader}></div>

                    {selectedEmployee ? (
                      <>
                        {/* 인사 정보 (60%) */}
                        <ContentCard className={styles.infoCard}>
                          <div className={styles.cardTitle}>인사 정보</div>
                          <div className={styles.formFields}>
                            <div className={styles.formRow}>
                              <label className={styles.label}>상위부서</label>
                              <select
                                className={styles.select}
                                value={selectedEmployee?.parentDepartment || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('parentDepartment', e.target.value);
                                }}
                              >
                                <option value="">선택</option>
                                {parentDepartmentList.map(function (department) {
                                  return (
                                    <option key={department} value={department}>
                                      {department}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className={styles.formRow}>
                              <label className={styles.label}>부서</label>
                              <select
                                className={styles.select}
                                value={selectedEmployee?.department || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('department', e.target.value);
                                }}
                                disabled={!selectedEmployee?.parentDepartment}
                              >
                                <option value="">선택</option>
                                {filteredTeamListByParent.map(function (team) {
                                  return (
                                    <option key={team.teamCode} value={team.team}>
                                      {team.team}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {showDepartment2 && (
                              <div className={styles.formRow}>
                                <label className={styles.label}>부서2</label>
                                <select
                                  className={styles.select}
                                  value={selectedEmployee?.department2 || ''}
                                  onChange={function (e) {
                                    handleEmployeeInfoChange('department2', e.target.value);
                                  }}
                                >
                                  <option value="">선택</option>
                                  {level3Options.map(function (team) {
                                    return (
                                      <option key={team.teamCode} value={team.team}>
                                        {team.team}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                            )}
                            {showDepartment3 && (
                              <div className={styles.formRow}>
                                <label className={styles.label}>부서3</label>
                                <select
                                  className={styles.select}
                                  value={selectedEmployee?.department3 || ''}
                                  onChange={function (e) {
                                    handleEmployeeInfoChange('department3', e.target.value);
                                  }}
                                >
                                  <option value="">선택</option>
                                  {level4Options.map(function (team) {
                                    return (
                                      <option key={team.teamCode} value={team.team}>
                                        {team.team}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                            )}
                            <div className={styles.formRow}>
                              <label className={styles.label}>이름</label>
                              <input
                                type="text"
                                className={styles.input}
                                value={selectedEmployee?.name || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('name', e.target.value);
                                }}
                              />
                            </div>
                            <div
                              className={styles.formRow}
                              style={
                                showEmployeeNumberError ? { alignItems: 'flex-start' } : undefined
                              }
                            >
                              <label className={styles.label} style={{ marginTop: '7px' }}>
                                사원번호
                              </label>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flex: 1,
                                  gap: '4px',
                                  minWidth: 0,
                                }}
                              >
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={8}
                                  className={styles.input}
                                  value={employeeNumberDisplay}
                                  onChange={function (e) {
                                    const digitsOnly = e.target.value
                                      .replace(/\D/g, '')
                                      .slice(0, 8);
                                    handleEmployeeInfoChange('employeeNumber', digitsOnly);
                                  }}
                                />
                                {showEmployeeNumberError && (
                                  <span className={styles.employeeNumberError}>
                                    *사원번호는 8자리로 입력해야합니다.
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className={styles.formRow}>
                              <label className={styles.label}>직급</label>
                              <select
                                className={styles.select}
                                value={selectedEmployee?.position || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('position', e.target.value);
                                }}
                              >
                                <option value="">선택</option>
                                {rankList.map(function (rank) {
                                  return (
                                    <option key={rank.code} value={String(rank.code)}>
                                      {rank.name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className={styles.formRow}>
                              <label className={styles.label}>직책</label>
                              <select
                                className={styles.select}
                                value={selectedEmployee?.jobTitle || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('jobTitle', e.target.value);
                                }}
                              >
                                <option value="">선택</option>
                                {positionList.map(function (position) {
                                  return (
                                    <option key={position.code} value={String(position.code)}>
                                      {position.name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className={styles.formRow}>
                              <label className={styles.label}>재직상태</label>
                              <select
                                className={styles.select}
                                value={selectedEmployee?.employmentStatus || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('employmentStatus', e.target.value);
                                }}
                              >
                                <option value="">선택</option>
                                <option value="재직">재직</option>
                                <option value="휴직">휴직</option>
                                <option value="퇴직">퇴직</option>
                              </select>
                            </div>
                            <div
                              className={styles.formRow}
                              style={showFimsEmpNoError ? { alignItems: 'flex-start' } : undefined}
                            >
                              <label className={styles.label} style={{ marginTop: '7px' }}>
                                FIMS 사번
                              </label>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flex: 1,
                                  gap: '4px',
                                  minWidth: 0,
                                }}
                              >
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={8}
                                  className={styles.input}
                                  value={fimsEmpNoDisplay}
                                  onChange={function (e) {
                                    const digitsOnly = e.target.value
                                      .replace(/\D/g, '')
                                      .slice(0, 8);
                                    handleEmployeeInfoChange('fimsEmpNo', digitsOnly);
                                  }}
                                />
                                {showFimsEmpNoError && (
                                  <span className={styles.employeeNumberError}>
                                    *FIMS 사원번호는 8자리로 입력해야합니다.
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className={styles.formRow}>
                              <label className={styles.label}></label>
                              <div className={styles.checkboxGroup}>
                                <div className={styles.checkboxWrapper}>
                                  <input
                                    type="checkbox"
                                    id="notEnforcerCheckbox"
                                    checked={selectedEmployee?.notEnforcer === true}
                                    onChange={(e) => {
                                      handleEmployeeInfoChange('notEnforcer', e.target.checked);
                                    }}
                                  />
                                  <label htmlFor="notEnforcerCheckbox">참조자 제외</label>
                                </div>
                                <div className={styles.checkboxWrapper}>
                                  <input
                                    type="checkbox"
                                    id="allowConcurrentLoginCheckbox"
                                    checked={selectedEmployee?.allowConcurrentLogin === true}
                                    onChange={(e) => {
                                      handleEmployeeInfoChange(
                                        'allowConcurrentLogin',
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                  <label htmlFor="allowConcurrentLoginCheckbox">
                                    동시접속 허용
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </ContentCard>

                        {/* 시스템 정보 (40%) */}
                        <ContentCard className={styles.infoCard}>
                          <div className={styles.cardTitle}>시스템 정보</div>
                          <div className={styles.formFields}>
                            <div className={styles.formRow}>
                              <label className={styles.label}>아이디</label>
                              <div className={styles.inputWithCheckbox}>
                                <input
                                  type="text"
                                  className={styles.input}
                                  value={selectedEmployee?.userId || ''}
                                  onChange={function (e) {
                                    handleEmployeeInfoChange('userId', e.target.value);
                                  }}
                                />
                                <div className={styles.checkboxWrapper}>
                                  <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    id="adminCheckbox"
                                    checked={selectedEmployee?.isAdmin || false}
                                    onChange={function (e) {
                                      handleEmployeeInfoChange('isAdmin', e.target.checked);
                                    }}
                                  />
                                  <label htmlFor="adminCheckbox" className={styles.checkboxLabel}>
                                    관리자
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className={styles.formRow} style={{ alignItems: 'flex-start' }}>
                              <label className={styles.label} style={{ marginTop: '7px' }}>
                                비밀번호
                              </label>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flex: 1,
                                  gap: '4px',
                                  minWidth: 0,
                                }}
                              >
                                <input
                                  type="text"
                                  className={styles.input}
                                  value={selectedEmployee?.password || ''}
                                  onChange={function (e) {
                                    handleEmployeeInfoChange('password', e.target.value);
                                  }}
                                />
                                {/* 비밀번호 생성 규칙 안내 (필요 시 주석 해제) */}
                                {/* <span style={{ fontSize: '13px', color: '#828282' }}>
                                  • 영문, 숫자, 특수문자 혼합 8자 이상
                                </span> */}
                              </div>
                            </div>
                            <div className={styles.notice}>
                              ※ 비밀번호 재설정 시 해당 계정의 로그인 잠금 상태가 해제됩니다.
                            </div>
                            <div className={styles.formRow}>
                              <label className={styles.label}>사내 이메일</label>
                              <input
                                type="email"
                                className={styles.input}
                                value={selectedEmployee?.email || ''}
                                onChange={function (e) {
                                  handleEmployeeInfoChange('email', e.target.value);
                                }}
                              />
                            </div>
                            {/* 저장 버튼 */}
                            <div className={styles.saveButtonWrapper}>
                              <Button
                                type="button"
                                variant="primary"
                                className={styles.saveButton}
                                onClick={handleSave}
                              >
                                저장
                              </Button>
                            </div>
                          </div>
                        </ContentCard>
                      </>
                    ) : (
                      <div className={styles.emptyState}>
                        임직원을 선택하면 상세 정보를 확인할 수 있습니다
                      </div>
                    )}
                  </>
                }
              />
            </ContentCard>
          ),
        }}
      />
      <Confirm
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
        cancelText="아니오"
        confirmText="예"
      />
      {/* LNB/로그아웃 클릭 시 저장 없이 이동 확인 */}
      <Confirm
        isOpen={Boolean(pendingLeave)}
        message="작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </AdminLayout>
  );
}
